import { DEFAULT_LOCALE, LANGUAGES_BY_BRAND } from '../i18n'
import {
  BRAND_ABBREVIATON_EUSKALTEL,
  BRAND_ABBREVIATON_MUNDO_R,
  BRAND_ABBREVIATON_TELECABLE,
  COOKIE_DOMAIN_LABELS,
  COOKIE_PAIR_SEPARATOR,
  COOKIE_PATH,
  COOKIE_SAME_SITE_LAX,
  COOKIE_SECURE,
  DATA_URI_PREFIX,
  JWT_EXP_TO_MS,
  LOCAL_STORAGE_ITEMS__LOCALE,
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  LOCALE_COOKIE_NAME,
  LOCALE_QUERY_PARAM,
  PROTOCOL_HTTPS,
  SVG_DATA_URI_PREFIX,
} from './constants'

const DEFAULT_REDIRECT_URI_BY_BRAND = {
  [BRAND_ABBREVIATON_EUSKALTEL]: 'https://portalclientes.euskaltel.com/',
  [BRAND_ABBREVIATON_MUNDO_R]: 'https://portalclientes.mundo-r.com/',
  [BRAND_ABBREVIATON_TELECABLE]: 'https://portalclientes.telecable.es/',
}

// La configuración de marca sirve los logos como markup SVG en crudo o como data URI ya montado
// (Telecable envía un PNG en base64), así que solo hay que envolver el SVG.
export const getLogoSrc = (logo) => {
  if (!logo) return undefined

  return logo.startsWith(DATA_URI_PREFIX) ? logo : `${SVG_DATA_URI_PREFIX}${encodeURIComponent(logo)}`
}

export const getRedirectUri = (brandAbbreviation) => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('redirect_uri') || urlParams.get('redirect_url') || DEFAULT_REDIRECT_URI_BY_BRAND[brandAbbreviation] || window.location.origin
}

const BRAND_BY_ABBREVIATION = {
  [BRAND_ABBREVIATON_EUSKALTEL]: 'euskaltel',
  [BRAND_ABBREVIATON_TELECABLE]: 'telecable',
  [BRAND_ABBREVIATON_MUNDO_R]: 'r',
}

export const getMarcaComercial = (brand) => BRAND_BY_ABBREVIATION[brand?.abbreviation]

// Devuelve el dominio registrable de la marca (`.euskaltel.com`, `.mundo-r.com`, ...) para que la
// cookie de idioma sea visible desde todos sus frontales. En hostnames sin subdominio (`localhost`)
// no hay dominio padre al que subir, así que se devuelve null y solo se usa localStorage.
export const getSharedCookieDomain = () => {
  const labels = window.location.hostname.split('.')

  return labels.length > COOKIE_DOMAIN_LABELS ? `.${labels.slice(-COOKIE_DOMAIN_LABELS).join('.')}` : null
}

export const getLocaleCookie = () => {
  const prefix = `${LOCALE_COOKIE_NAME}=`
  const cookie = document.cookie.split(COOKIE_PAIR_SEPARATOR).find((item) => item.startsWith(prefix))

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null
}

export const getLocaleQueryParam = () => {
  const locale = new URLSearchParams(window.location.search).get(LOCALE_QUERY_PARAM)

  return locale ? locale.trim().toLowerCase() : null
}

export const setLocaleCookie = (locale) => {
  const cookieDomain = getSharedCookieDomain()
  const attributes = [
    `${LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}`,
    `path=${COOKIE_PATH}`,
    `max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`,
    `SameSite=${COOKIE_SAME_SITE_LAX}`,
  ]

  if (cookieDomain) {
    attributes.push(`domain=${cookieDomain}`)
  }

  // Una cookie con Secure se descarta sobre http, que es como se sirve el entorno local
  if (window.location.protocol === PROTOCOL_HTTPS) {
    attributes.push(COOKIE_SECURE)
  }

  document.cookie = attributes.join(COOKIE_PAIR_SEPARATOR)
}

// Deja el idioma activo en los dos almacenes: la cookie lo propaga a los demás frontales de la
// marca y localStorage lo mantiene disponible para los componentes que lo leen directamente.
export const persistLocale = (locale) => {
  localStorage.setItem(LOCAL_STORAGE_ITEMS__LOCALE, locale)
  setLocaleCookie(locale)
}

// El idioma de la url manda sobre todo lo demás porque es el que eligió el usuario al pedir la
// recuperación de contraseña. Después la cookie, que trae el idioma elegido en el portal de empresa
// o en OAuth; localStorage queda como respaldo para el entorno local, sin dominio padre.
export const getLanguageLocale = (brand) => {
  const isSupported = (locale) =>
    !!brand.abbreviation &&
    !!locale &&
    locale !== 'undefined' &&
    LANGUAGES_BY_BRAND[brand.abbreviation]?.some((item) => item.code === locale)

  const queryLocale = getLocaleQueryParam()
  if (isSupported(queryLocale)) return queryLocale

  const cookieLocale = getLocaleCookie()
  if (isSupported(cookieLocale)) return cookieLocale

  const storedLocale = localStorage.getItem(LOCAL_STORAGE_ITEMS__LOCALE)
  if (isSupported(storedLocale)) return storedLocale

  return DEFAULT_LOCALE
}

export const decodeJWT = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null
    }

    const payload = token.split('.')[1]

    if (!payload) {
      return null
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')

    return JSON.parse(atob(paddedBase64))
  } catch {
    return null
  }
}

export const isJWTExpired = (token) => {
  const exp = decodeJWT(token)?.exp

  if (!exp) {
    return false
  }

  return Date.now() >= exp * JWT_EXP_TO_MS
}

export const checkGetResult = (data) => data?.resultado?.codigo === 0
